import { useEffect, useState } from "react";
import { FileSpreadsheet, Package } from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { Modal } from "@/components/ui/Modal";
import { formatDate, formatNumber } from "@/utils/format";
import { CotacaoCard, fetchQuotationItems, type QuotationItemRow } from "@/services/suprimentos";

export function CotacaoItensModal({
  cotacao,
  onClose,
}: {
  cotacao: CotacaoCard;
  onClose: () => void;
}) {
  const [items, setItems] = useState<QuotationItemRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchQuotationItems(cotacao.purchaseQuotationId)
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((e) => {
        if (!cancelled) {
          setItems([]);
          setError(e instanceof Error ? e.message : "Erro ao carregar itens da cotação.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [cotacao.purchaseQuotationId]);

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-amber-50 flex-shrink-0 border border-amber-100">
            <FileSpreadsheet className="w-5 h-5 text-amber-700" />
          </div>
          <div className="min-w-0">
            <p className="text-base font-bold text-slate-800 leading-tight line-clamp-2">{cotacao.label}</p>
            <p className="text-xs text-slate-400 font-mono truncate">#{cotacao.purchaseQuotationId}</p>
          </div>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">Fornecedor</p>
            <p className="text-xs font-semibold text-slate-800 mt-0.5 line-clamp-2">{cotacao.supplierName || "—"}</p>
          </div>
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">Data</p>
            <p className="text-xs font-semibold text-slate-800 mt-0.5">
              {cotacao.date ? formatDate(cotacao.date) : "—"}
            </p>
          </div>
          <div className="bg-amber-50 rounded-xl px-3 py-2.5 border border-amber-100">
            <p className="text-[10px] text-amber-800 uppercase tracking-wide font-semibold">Status</p>
            <p className="text-xs font-bold text-amber-950 mt-0.5 line-clamp-2">{cotacao.status || "—"}</p>
          </div>
          <div className="bg-slate-50 rounded-xl px-3 py-2.5 border border-slate-100">
            <p className="text-[10px] text-slate-500 uppercase tracking-wide font-semibold">Itens (card)</p>
            <p className="text-xs font-bold text-slate-800 mt-0.5 tabular-nums">{formatNumber(cotacao.itemCount)}</p>
          </div>
        </div>

        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>
        )}

        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-slate-400">
            <Loader size="md" label="Carregando itens da cotação…" />
          </div>
        ) : !items?.length ? (
          <div className="flex flex-col items-center gap-2 py-10 text-slate-400">
            <Package className="w-8 h-8 opacity-40" />
            <p className="text-sm">Nenhum item retornado para esta cotação.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-slate-200">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-[1]">
                <tr>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-12">
                    #
                  </th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Produto</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide min-w-[160px]">
                    Descrição
                  </th>
                  <th className="text-right px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide">Qtd.</th>
                  <th className="text-left px-3 py-2.5 font-semibold text-slate-500 uppercase tracking-wide w-14">Un.</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((it, idx) => (
                  <tr key={`${it.itemNumber}-${it.productId}-${idx}`} className="hover:bg-slate-50/80">
                    <td className="px-3 py-2 text-slate-600 tabular-nums font-medium">{it.itemNumber || "—"}</td>
                    <td className="px-3 py-2 text-slate-600 font-mono text-[11px]">{it.productId || "—"}</td>
                    <td className="px-3 py-2 text-slate-800">{it.description || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums text-slate-700">{it.quantity || "—"}</td>
                    <td className="px-3 py-2 text-slate-600">{it.unit || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}
